import Container from '@/components/Container';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { Suspense, useCallback, useEffect } from 'react';
import { LoadingContainer } from '../../components/loading-container';
import PageHeader from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import Tabs from '@/components/ui/tabs';
import { ResourceCard } from '@/components/ResourceCard';
import { ResourceHubItem } from '@/pages/resource-hub/shared/types';
import { Course, KnowledgeBase, Podcast } from '@edwin-group/people-graphql';
import { BookmarkItem } from '@/components/Bookmark/useBookmark';
import { useBookmarkPage } from './hooks/useBookmarkPage';
import { PaginationControls } from '@/pages/bookmarks/components/PaginationControls';
import { CourseBookmarkCard } from '@/pages/bookmarks/components/course';
import { PodcastBookmarkCard } from '@/pages/bookmarks/components/podcast';
import { KnowledgeBaseBookmarkCard } from '@/pages/bookmarks/components/knowledge-base';

function BookmarkPageComponent() {
  const {
    bookmarkPage,
    allBookmarks,
    filteredBookmarks,
    isLoading,
    filters,
    pagination,
    searchValue,
    onSearchChange,
    tabs,
    handleTabChange,
    onPageChange,
    refetch,
  } = useBookmarkPage();

  const searchPlaceholder = bookmarkPage?.searchPlaceholder || 'Search';
  const loadingText = bookmarkPage?.loadingText || 'Loading bookmarks...';
  const emptyStateText = bookmarkPage?.emptyStateText || 'No bookmarks found';
  const removeFromBookmarksText = bookmarkPage?.removeFromBookmarksText || 'Remove from bookmarks';
  const goToResourceText = bookmarkPage?.goToResourceText || 'Go to resource';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pagination.page]);

  const renderCard = useCallback(
    (bookmark: BookmarkItem) => {
      const { category, metadata, relationId } = bookmark;

      switch (category) {
        case 'COURSE':
          return (
            <CourseBookmarkCard
              key={relationId}
              course={metadata as Course}
              onRemoveFromBookmark={refetch}
              removeFromBookmarksText={removeFromBookmarksText}
              openButtonTxt={bookmarkPage?.openButtonTxt || 'Open'}
            />
          );
        case 'PODCAST':
          return (
            <PodcastBookmarkCard
              key={relationId}
              podcast={metadata as Podcast}
              onRemoveFromBookmark={refetch}
              removeLabel={removeFromBookmarksText}
            />
          );
        case 'KNOWLEDGE_BASE':
          return (
            <KnowledgeBaseBookmarkCard
              key={relationId}
              knowledgebase={metadata as KnowledgeBase}
              onRemoveFromBookmark={refetch}
              removeFromBookmarksText={removeFromBookmarksText}
              openButtonTxt={bookmarkPage?.openButtonTxt || 'Learn more'}
            />
          );
        case 'RESOURCE':
          return (
            <ResourceCard
              key={relationId}
              resource={metadata as unknown as ResourceHubItem}
              onRemoveFromBookmark={refetch}
              linkText={goToResourceText}
              removeFromBookmarksText={removeFromBookmarksText}
            />
          );
        default:
          return null;
      }
    },
    [bookmarkPage, removeFromBookmarksText, goToResourceText, refetch],
  );

  return (
    <>
      <PageHeader title={bookmarkPage?.pageTitle ?? 'Bookmarks'} description={bookmarkPage?.description} />
      <Container className="pb-sh-60" addMargin>
        <div className="col-span-12 min-h-[30rem]">
          <section className="mb-sh-30">
            <AnimatedFadeInComponent>
              <Input
                theme="search"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                className="max-w-full"
                aria-label={searchPlaceholder}
              />
            </AnimatedFadeInComponent>
          </section>

          <section className="mb-sh-30">
            <Tabs
              tabs={tabs}
              setTabs={handleTabChange}
              tabsClassesNames="!justify-start !gap-6"
              textClassNames="!text-sh-xs"
            />
          </section>

          {isLoading ? (
            <div className="flex min-h-[400px] items-center justify-center">
              <p className="font-haffer text-sh-md text-gray-500">{loadingText}</p>
            </div>
          ) : allBookmarks.length === 0 ? (
            <div className="flex min-h-[400px] items-center justify-center">
              <p className="font-haffer text-sh-md text-gray-500">{emptyStateText}</p>
            </div>
          ) : (
            <>
              <section className="mb-sh-60">
                <AnimatedFadeInComponent>
                  <div
                    className={
                      filters.category === 'PODCAST' ||
                      filters.category === 'KNOWLEDGE_BASE' ||
                      filters.category === 'COURSE'
                        ? 'grid grid-cols-1 gap-6'
                        : filters.category === 'RESOURCE'
                          ? 'grid grid-cols-1 gap-[25px] md:grid-cols-2'
                          : 'grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'
                    }>
                    {filteredBookmarks.map((bookmark) => renderCard(bookmark))}
                  </div>
                </AnimatedFadeInComponent>
              </section>

              <div className="border-top: 1px solid var(--Gray-200, #E9EAEB);"></div>

              <PaginationControls page={pagination.page} pageCount={pagination.pageCount} onPageChange={onPageChange} />
            </>
          )}
        </div>
      </Container>
    </>
  );
}

export function BookmarkPageScreen() {
  return (
    <Suspense fallback={<LoadingContainer title="Loading bookmarks..." />}>
      <BookmarkPageComponent />
    </Suspense>
  );
}
